import { useAtomValue } from 'jotai';
import { useMemo } from 'react';
import * as yup from 'yup';
import {
  FundWaterfallResponse,
  PER_CLASS_PROCEEDS_KEYS,
} from '../../../data-models/fund-waterfall.data-model';
import {
  createDistributionSegment,
  DistributionSegment,
  distributionSegmentSchema,
  TierFieldToLabel,
} from '../../../schemas/FundWaterfall.schema';
import { selectedDateStringFPState, selectedFundIdStateFP } from '../state/FPState';
import { waterfallDataAtom } from './FPWaterfallData3';

function waterfallResultsGridDataFields() {
  return {
    tierName: yup.string().nullable().label('Tier'),
    total: yup.number().nullable().customMeta({ formatter: 'currency0Dp' }).label(TierFieldToLabel.total),
    // Pool columns are paired with their formula columns (only shown when per-class proceeds are configured)
    lpRemaining: yup.number().nullable().customMeta({ formatter: 'currency0Dp' }).label('LP Pool'),
    lp: yup.number().nullable().customMeta({ formatter: 'currency0Dp' }).label(TierFieldToLabel.lp),
    slpRemaining: yup.number().nullable().customMeta({ formatter: 'currency0Dp' }).label('SLP Pool'),
    slp: yup.number().nullable().customMeta({ formatter: 'currency0Dp' }).label(TierFieldToLabel.slp),
    gpRemaining: yup.number().nullable().customMeta({ formatter: 'currency0Dp' }).label('GP Pool'),
    gp: yup.number().nullable().customMeta({ formatter: 'currency0Dp' }).label(TierFieldToLabel.gp),
    carry: yup.number().nullable().customMeta({ formatter: 'currency0Dp' }).label(TierFieldToLabel.carry),
    remaining: yup
      .number()
      .nullable()
      .customMeta({ formatter: 'currency' })
      .label(TierFieldToLabel.remaining),

    tierOrder: yup.number().nullable(),
    visualization: yup.array().of(distributionSegmentSchema()).nullable().label('Visualization'),
  };
}
export function waterfallResultsGridDataSchema() {
  return yup.object(waterfallResultsGridDataFields());
}
export type WaterfallResultsGridData = yup.InferType<ReturnType<typeof waterfallResultsGridDataSchema>>;
function waterfallResultsSummaryFields() {
  return {
    lpNav: yup.number().nullable().customMeta({ formatter: 'currency0Dp' }).label('LP NAV'),
    slpNav: yup.number().nullable().customMeta({ formatter: 'currency0Dp' }).label('SLP NAV'),
    gpNav: yup.number().nullable().customMeta({ formatter: 'currency0Dp' }).label('GP NAV'),
  };
}
export function waterfallResultsSummarySchema() {
  return yup.object(waterfallResultsSummaryFields());
}
export type WaterfallResultsSummary = yup.InferType<ReturnType<typeof waterfallResultsSummarySchema>>;

export const PER_CLASS_POOL_KEYS = ['lpRemaining', 'slpRemaining', 'gpRemaining'] as const;

export function hasPerClassProceedsConfig(data: FundWaterfallResponse): boolean {
  const config = data.configuration;
  return PER_CLASS_PROCEEDS_KEYS.some((key) => config[key] != null && config[key] !== '');
}

export function useWaterfallResultsGrid() {
  const fundId = useAtomValue(selectedFundIdStateFP);
  const asOfDate = useAtomValue(selectedDateStringFPState);
  const { data } = useAtomValue(waterfallDataAtom({ fundId: fundId ?? -1, asOfDate }));
  return useMemo(() => {
    if (!data) {
      return null;
    }
    return {
      tierData: getWaterfallResultsGridData(data),
      summaryData: {
        gpNav: data.results?.gpNav,
        lpNav: data.results?.lpNav,
        slpNav: data.results?.slpNav,
      },
      hasPerClassProceeds: hasPerClassProceedsConfig(data),
    };
  }, [data]);
}

export function getWaterfallResultsGridData(data: FundWaterfallResponse): WaterfallResultsGridData[] {
  const { results, configuration } = data;
  if (!results) {
    return [];
  }
  const initialTier: Partial<WaterfallResultsGridData> = {
    total: results.totalProceeds,
    tierName: 'Initial',
    lpRemaining: results.lpTotalProceeds,
    slpRemaining: results.slpTotalProceeds,
    gpRemaining: results.gpTotalProceeds,
  };
  const tiers = Object.entries(results.tiers)
    .sort(([a], [b]) => Number(a) - Number(b))
    .map(([tierOrder, tier]) => ({
      tierName: configuration.tiers[Number(tierOrder)]?.tierName ?? `Tier ${tierOrder}`,
      lp: tier.lp,
      slp: tier.slp,
      gp: tier.gp,
      carry: tier.carry,
      remaining: tier.remaining,
      tierOrder: Number(tierOrder),
      total: tier.total,
      lpRemaining: tier.lpRemaining ?? null,
      slpRemaining: tier.slpRemaining ?? null,
      gpRemaining: tier.gpRemaining ?? null,
    }));

  return [initialTier, ...tiers].reduce((acc, curr) => {
    const prev = acc.at(-1);
    const visualization = getDistributions(
      curr as WaterfallResultsGridData,
      initialTier.total ?? 0,
      prev?.visualization ?? []
    );
    return [...acc, { ...curr, visualization }];
  }, [] as WaterfallResultsGridData[]);
}

export function getDistributions(
  data: WaterfallResultsGridData,
  initialDistributableProceeds: number,
  prevSegments: DistributionSegment[] = []
): DistributionSegment[] | null {
  const { gp, lp, slp, carry } = data;
  const lpSegmentValue = initialDistributableProceeds ? (lp ?? 0) / initialDistributableProceeds : 0;
  const gpSegmentValue = initialDistributableProceeds ? (gp ?? 0) / initialDistributableProceeds : 0;
  const slpSegmentValue = initialDistributableProceeds ? (slp ?? 0) / initialDistributableProceeds : 0;
  const carrySegmentValue = initialDistributableProceeds ? (carry ?? 0) / initialDistributableProceeds : 0;

  const gpSegment = createDistributionSegment({
    percentValue: gpSegmentValue,
    highlight: true,
    type: 'gp' as const,
  });
  const lpSegment = createDistributionSegment({
    percentValue: lpSegmentValue,
    highlight: true,
    type: 'lp' as const,
  });
  const slpSegment = createDistributionSegment({
    percentValue: slpSegmentValue,
    highlight: true,
    type: 'slp' as const,
  });
  const carrySegment = createDistributionSegment({
    percentValue: carrySegmentValue,
    highlight: true,
    type: 'carry' as const,
  });

  const result: DistributionSegment[] = prevSegments.map((s) => ({ ...s, highlight: false }));
  result.push(gpSegment, lpSegment, slpSegment, carrySegment);
  return result;
}
